import { Check, Info, X, type LucideIcon } from 'lucide-react';
import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SPRING } from 'shared/components/motion/tokens';
import { cn } from 'shared/utils/cn';

type ToastTone = 'success' | 'info';

interface ToastInput {
  title: string;
  description?: string;
  tone?: ToastTone;
}

interface ToastItem extends Required<Omit<ToastInput, 'description'>> {
  id: number;
  description?: string;
}

const ToastContext = createContext<((toast: ToastInput) => void) | null>(null);

const DURATION_MS = 3800;
const MAX_VISIBLE = 3;

const TONE_STYLES: Record<ToastTone, { icon: LucideIcon; iconClass: string }> = {
  success: { icon: Check, iconClass: 'bg-success-soft text-success' },
  info: { icon: Info, iconClass: 'bg-accent-soft text-primary-dark' },
};

// Retorno visual das ações do modo apresentação: toda ação que no produto real chamaria
// a API (cobrar, convidar, aprovar…) confirma aqui o que teria acontecido.
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    ({ title, description, tone = 'success' }: ToastInput) => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, title, description, tone }].slice(-MAX_VISIBLE));
      window.setTimeout(() => dismiss(id), DURATION_MS);
    },
    [dismiss]
  );

  const value = useMemo(() => show, [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-4 bottom-20 z-50 flex flex-col items-center gap-2 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:items-end"
      >
        <AnimatePresence initial={false}>
          {toasts.map((toast) => {
            const tone = TONE_STYLES[toast.tone];
            return (
              <motion.div
                key={toast.id}
                layout
                role="status"
                initial={{ opacity: 0, y: 16, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, x: 24, transition: { duration: 0.2 } }}
                transition={SPRING}
                className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-2xl border border-border/60 bg-surface/95 p-4 shadow-card-hover backdrop-blur"
              >
                <span
                  className={cn(
                    'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold',
                    tone.iconClass
                  )}
                  aria-hidden="true"
                >
                  <tone.icon className="h-4 w-4" strokeWidth={2.5} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ink">{toast.title}</p>
                  {toast.description && <p className="mt-0.5 text-sm text-ink-secondary">{toast.description}</p>}
                </div>
                <button
                  type="button"
                  onClick={() => dismiss(toast.id)}
                  className="-mr-1 -mt-1 rounded-md p-1 text-ink-secondary hover:bg-chip hover:text-ink"
                  aria-label="Fechar aviso"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const toast = useContext(ToastContext);
  if (!toast) throw new Error('useToast precisa estar dentro de <ToastProvider>.');
  return toast;
}
